import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import LoadBoardSerializer from "../../serializers/LoadBoardSerializer";
import useLoadStatus from "../useLoadStatus/useLoadStatus";
import { LoadService } from "../../services/LoadService";
import { AlertContext } from "../../components/Dashboard/Dashboard";
import { getEtaStatus, getLoadExceptions, getEtaStatusHoverString } from "../../utils/dataHelpers";
import { MAXPAY_SYNC_CHANNEL, MAXPAY_SYNC_STORAGE_KEY, MAXPAY_UPDATED_EVENT } from "../../constants/syncConstants";

export default function useLoadBoardsReceiver(isLoggedIn) {
  const syncInstanceId = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [connection, setConnection] = useState(null);
  const [isLoadBoardLoading, setIsLoadBoardLoading] = useState(false);
  const [newcomer, setNewcomer] = useState({});
  const [allLoads, setAllLoads] = useState([]);
  const [newLoads, setNewLoads] = useState([]);
  const { loadStatuses } = useLoadStatus();

  const dispatchMaxPayEvent = useCallback((orderId, maxPay) => {
    if (!orderId) {
      return;
    }

    window.dispatchEvent(new CustomEvent(MAXPAY_UPDATED_EVENT, {
      detail: {
        orderId,
        maxPay,
      }
    }));
  }, []);

  const updateLoadsState = useCallback((orderId, field, value) => {
    if (!orderId || !field) {
      return;
    }

    const patchLoad = load => {
      if (load.orderId === orderId) {
        return {
          ...load,
          [field]: value,
        };
      }
      return load;
    };

    setAllLoads(old => old.map(patchLoad));
    setNewLoads(old => old.map(patchLoad));
  }, []);

  const publishMaxPaySync = useCallback((orderId, maxPay) => {
    if (!orderId) {
      return;
    }

    const payload = {
      type: "maxPayUpdated",
      orderId,
      maxPay,
      source: syncInstanceId.current,
      timestamp: Date.now(),
    };

    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel(MAXPAY_SYNC_CHANNEL);
      channel.postMessage(payload);
      channel.close();
    }

    localStorage.setItem(MAXPAY_SYNC_STORAGE_KEY, JSON.stringify(payload));
  }, []);

  const deleteNewLoads = (orderId) => {
    if (orderId) {
      const copyLoads = [...newLoads];
      copyLoads.forEach((newLoad, index, array) => {
        newLoad.orderId === orderId && array.splice(index, 1);
      });
      setNewLoads(copyLoads);
    }
  };

  const updateLoadField = useCallback((orderId, field, value) => {
    updateLoadsState(orderId, field, value);

    if (field === "maxPay") {
      dispatchMaxPayEvent(orderId, value);
      publishMaxPaySync(orderId, value);
    }
  }, [dispatchMaxPayEvent, publishMaxPaySync, updateLoadsState]);

  const getLoads = () => {
    const statuses = loadStatuses.map(status => status.id).join();

    setIsLoadBoardLoading(true);
    LoadService.GetLoads(instance, statuses).then(res => {
      res.forEach(load => {
        load.activeRow = false;
        load.etaStatus = getEtaStatus(load.brokerageStatus, load.newEta, load.originSchedArriveEarly, load.originSchedArriveLate, load.destinationSchedArriveEarly, load.destinationSchedArriveLate);
        load.exceptions = getLoadExceptions(load);
        load.etaStatusHoverString = getEtaStatusHoverString(load.etaStatus, load.exceptions);
      });
      setAllLoads(res);
      setIsLoadBoardLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoadBoardLoading(false);
      warning(error);
    });
  };

  useEffect(() => {
    if (loadStatuses.length > 0) {
      getLoads();
    }
  }, [loadStatuses]);

  useEffect(() => {
    if (newcomer?.movementId) {
      const allLoadsUpdated = [...allLoads];
      const newLoadsUpdated = [...newLoads];
      const newLoad = newcomer.load;
      newLoad.activeRow = false;
      newLoad.etaStatus = getEtaStatus(newLoad.brokerageStatus, newLoad.newEta, newLoad.originSchedArriveEarly, newLoad.originSchedArriveLate, newLoad.destinationSchedArriveEarly, newLoad.destinationSchedArriveLate);
      newLoad.exceptions = getLoadExceptions(newLoad);
      newLoad.etaStatusHoverString = getEtaStatusHoverString(newLoad.etaStatus, newLoad.exceptions);

      const index = allLoadsUpdated.findIndex(l => l.movementId == newcomer.movementId);
      if ((index >= 0 && newLoad.brokerageStatus !== "AVA") || newcomer.isDatAction) {
        allLoadsUpdated.splice(index, 1, newLoad);
      } else if (index >= 0 && newLoad.brokerageStatus === "AVA") {
        newLoad.newlyAdded = true;
        newLoad.timeHighlight = 30;

        newLoadsUpdated.push(newLoad);
        allLoadsUpdated.splice(index, 1);
        allLoadsUpdated.unshift(newLoad);
      } else {
        allLoadsUpdated.unshift(newLoad);
      }

      setNewLoads(newLoadsUpdated);
      setAllLoads(allLoadsUpdated);
    }
  }, [newcomer]);

  useEffect(() => {
    if (newLoads?.length > 0 && allLoads?.length > 0) {
      const interval = setInterval(() => {
        let allLoadsUpdated = [...allLoads];
        newLoads.forEach((newLoad, index, array) => {
          let timeLeft = newLoad.timeHighlight - 1;
          if (timeLeft >= 0) {
            newLoad.timeHighlight = timeLeft;
          } else {
            const indexLoad = allLoadsUpdated.findIndex(load => load.movementId == newLoad.movementId);
            if (indexLoad >= 0) { allLoadsUpdated[indexLoad].newlyAdded = false; }
            array.splice(index, 1);
            setAllLoads(allLoadsUpdated);
          }
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [newLoads, allLoads]);

  useEffect(() => {
    let channel = null;

    const handleIncomingMaxPaySync = payload => {
      if (!payload || payload.type !== "maxPayUpdated" || payload.source === syncInstanceId.current) {
        return;
      }

      updateLoadsState(payload.orderId, "maxPay", payload.maxPay);
      dispatchMaxPayEvent(payload.orderId, payload.maxPay);
    };

    const hasBroadcastChannel = typeof BroadcastChannel !== "undefined";

    if (hasBroadcastChannel) {
      channel = new BroadcastChannel(MAXPAY_SYNC_CHANNEL);
      channel.onmessage = event => {
        handleIncomingMaxPaySync(event?.data);
      };
    }

    const handleStorage = event => {
      if (event.key === MAXPAY_SYNC_STORAGE_KEY && event.newValue) {
        try {
          handleIncomingMaxPaySync(JSON.parse(event.newValue));
        } catch {
          // ignore malformed sync event
        }
      }
    };

    // Storage listener used as fallback when BroadcastChannel is unavailable
    if (!hasBroadcastChannel) {
      window.addEventListener("storage", handleStorage);
    }

    return () => {
      if (!hasBroadcastChannel) {
        window.removeEventListener("storage", handleStorage);
      }
      if (channel) {
        channel.close();
      }
    };
  }, [dispatchMaxPayEvent, updateLoadsState]);

  useEffect(() => {
    if (!connection && isLoggedIn) {
      const newConnection = new HubConnectionBuilder()
        .withUrl("/LoadBoardsNotifications")
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      newConnection.onreconnected(() => {
        newConnection.invoke("AddToGroup", "ChangeInLoad");
      });

      setConnection(newConnection);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    async function start() {
      try {
        if (connection.state === "Disconnected") {
          await connection.start();
        }

        if (!isLoggedIn) {
          await connection.invoke("RemoveFromGroup", "ChangeInLoad");
        } else {
          await connection.invoke("AddToGroup", "ChangeInLoad");
        }

        connection.on("ChangeInLoad", update => {         
          if (!update?.load) {
            return;
          }

          update.load = LoadBoardSerializer.deSerialize(update.load);

          if (update.load?.orderId && typeof update.load?.maxPay === "number") {
            updateLoadsState(update.load.orderId, "maxPay", update.load.maxPay);
            dispatchMaxPayEvent(update.load.orderId, update.load.maxPay);
          }

          if (update?.maxPayOnly) {
            return;
          }

          setNewcomer(update);
        });
      } catch (err) {
        console.error(err);
      }
    }

    const initiateConnections = async () => {
      if (connection && isLoggedIn) {
        start();
      } else if (connection && !isLoggedIn) {
        await connection.stop();
      }
    };

    initiateConnections();
  }, [connection, isLoggedIn, dispatchMaxPayEvent, updateLoadsState]);

  return {
    isLoadBoardLoading,
    allLoads,
    deleteNewLoads,
    updateLoadField,
    newcomer,
  };
}